import React, { useState, useCallback, useEffect } from "react";
import AddExbondheader from "./addexbondheader";
import Addexbondform from "./addexbondform";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/loadercontext";
import CancelButton from "../common/ui/cancelbutton";
import Button from "../common/ui/button";
import { useAuth } from "../../context/AuthContext";
import WarningModal from "../common/ui/warningmodel";

export default function Addexbond() {
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const [resultants, setResultants] = useState([]);
  const [loadingResultants, setLoadingResultants] = useState(false);
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [binumber, setBINumber] = useState([]);
  const [loadingBINumber, setLoadingBINumber] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [availableWeights, setAvailableWeights] = useState({}); // Store available weights by material ID
  const { logout } = useAuth();
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const exbondExportOptions = [
    { label: "Export", value: "export" },
    { label: "Exbond", value: "exbond" },
  ];
  const [records, setRecords] = useState([]);

  const [formData, setFormData] = useState({
    biNumber: "",
    beNumber: "",
    beDate: "",
    section: "",
    exbondExport: "",
    resultant: "",
    material: "",
    dutyExbond: "",
    dollar: "",
    rate: "",
    weight: "",
    invoiceValue: "",
    customer: "",
    inbondBeDate: "",
    dutypaid: null, // store boolean
  });

  // === Utilities ===
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const fetchResultant = useCallback(async () => {
    try {
      setLoadingResultants(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_NAME}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }


      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setResultants(
          result.data.map((m) => ({
            label: `${m.material_short_code.toUpperCase()}`,
            value: m.material_id,
            nameOnly: m.material_name
          }))
        );
      } else {
        setResultants([]);
      }
    } catch (error) {
      console.error("Error fetching materials:", error);
      setWarningMessage("Failed to fetch materials");
      setWarningVisible(true);

      setResultants([]);
    } finally {
      setLoadingResultants(false);
    }
  }, [logout]);

  const fetchSections = useCallback(async () => {
    try {
      setLoadingSections(true);


      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );
      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTIONS.GET_ALL}`,
            { method: "PUT", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setSections(
          result.data.map((s) => ({
            label: s.section_name,
            value: s.id,
          }))
        );
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  }, [logout]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CUSTOMERS.GET_ALL}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setCustomers(
          result.data.map((customer) => ({
            label: customer.name,
            value: customer.id,
          }))
        );
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, [logout]);

  const fetchBINumber = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();
    try {
      setLoadingBINumber(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_BI_NUMBER}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INBOND.GET_BI_NUMBER}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }

      const result = await response.json();
      if (result.status === 200 && Array.isArray(result.data)) {
        setBINumber(
          result.data.map((item) => ({
            label: item.binumber.toUpperCase(),
            value: item.id,
          }))
        );
      } else {
        setBINumber([]);
      }
    } catch (error) {
      console.error("Error fetching BI number:", error);
      setBINumber([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 1000 - elapsed);

      setTimeout(() => {
        setLoading(false);
      }, remainingDelay);
      setLoadingBINumber(false);
    }
  }, [setLoading, logout]);

  const fetchMaterialInbondDate = useCallback(async (inbond_master_id) => {
    if (!inbond_master_id) return;
    try {
      setLoading(true);
      setLoadingMaterials(true);
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.GET_MATERIAL_INBOND_DATE(inbond_master_id)}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.GET_MATERIAL_INBOND_DATE(inbond_master_id)}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }
      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data) && result.data.length > 0) {
        const materialList = result.data.map((item) => ({
          label: `${item.material_short_code.toUpperCase()}`,
          value: item.inbond_child_id, // ✅ Use inbond_child_id as value
          weight: item.material_weight,
          beDate: item.inbond_date,
          shortCode: item.material_short_code,
          nameOnly: item.material_name
        }));

        setMaterials(materialList);

        const firstInbondDate = result.data[0].inbond_date;

        setFormData((prev) => ({
          ...prev,
          material: "",
          weight: "",
          beDate: "",
          inbondBeDate: firstInbondDate,
        }));
      } else {
        setWarningMessage("No material data found for selected BI Number");
        setWarningVisible(true);
        setMaterials([]);
        setFormData((prev) => ({
          ...prev,
          material: "",
          weight: "",
          beDate: "",
          inbondBeDate: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching material and BE date:", error);
      setWarningMessage("Failed to fetch material and BE date");
      setWarningVisible(true);
      setMaterials([]);
      setFormData((prev) => ({
        ...prev,
        material: "",
        weight: "",
        beDate: "",
        inbondBeDate: "",
      }));
    } finally {
      setLoadingMaterials(false);
      setLoading(false);
    }
  }, [setLoading, logout]);

  // ✅ Fetch available weight for selected material
  const fetchWeightAndDutyByMaterial = useCallback(async (inbond_child_id) => {
    if (!inbond_child_id) {
      return;
    }

    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.GET_INBOND_WEIGHT_DUTY(
        inbond_child_id
      )}`;

      // Helper function for fetch
      const callApi = async () =>
        await fetch(url, {
          method: "GET",
          headers: getHeaders(true),
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      if (!response.ok) {
        setWarningMessage("Failed to fetch weight/duty data from server.");
        setWarningVisible(true);
        return;
      }

      const result = await response.json();

      if (result.status === 200 && result.data) {
        const { inbond, exbond } = result.data;

        const inbondWeight = Number(inbond?.inbond_weight || 0);
        const exbondWeight = Number(exbond?.exbond_weight || 0);
        const inbondDuty = Number(inbond?.duty_inbond_amount_inr || 0);
        const exbondDuty = Number(exbond?.duty_exbond_amount_inr || 0);
        const availableWeight = inbondWeight - exbondWeight;

        // ✅ Store available weight for this material
        setAvailableWeights((prev) => ({
          ...prev,
          [inbond_child_id]: availableWeight,
        }));

        // Show warning if exbond duty exceeds inbond duty
        if (exbondDuty > inbondDuty) {
          setWarningMessage(`Exbond duty (₹${exbondDuty}) is higher than Inbond duty (₹${inbondDuty}). Please verify.`);
          setWarningVisible(true);
        }

        console.log(`Available weight for material ${inbond_child_id}: ${availableWeight} kg`);
      } else {
        setWarningMessage("Invalid response from server");
        setWarningVisible(true);
      }
    } catch (error) {
      console.error("Error fetching weight/duty:", error);
    }
  }, [logout]);

  useEffect(() => {
    fetchResultant();
    fetchSections();
    fetchCustomers();
    fetchBINumber();
  }, [fetchResultant, fetchSections, fetchCustomers, fetchBINumber]);

  const handleSubmit = async () => {
    if (records.length === 0) {
      setWarningMessage("Please add at least one entry before submitting!");
      setWarningVisible(true);
      return;
    }

    try {
      setLoading(true);

      // Calculate totals
      const totalDutyExbond = records.reduce(
        (sum, r) => sum + (Number(r.dutyExbond) || 0),
        0
      );
      const totalWeight = records.reduce(
        (sum, r) => sum + (Number(r.weight) || 0),
        0
      );
      // Map records to API format
      const exbondchild = records.map((record) => ({
        inbond_master_id: Number(record.biNumber),
        material_master_id: Number(record.resultant), // keep ID for backend reference
        section_master_id: Number(record.section),
        customer_master_id: Number(record.customer),
        inbond_child_id: Number(record.material),
        be_number: String(record.beNumber),
        be_date: String(record.beDate),
        type: String(record.exbondExport),
        resultant: String(record.resultantName), // ✅ send resultant name (string)
        duty_exbond_amount_inr: Number(record.dutyExbond),
        dollar_inr: Number(record.dollar),
        rate: Number(record.rate),
        weight: Number(record.weight),
        invoice_amount_inr: Number(record.invoiceValue),
        is_duty_paid: Boolean(record.dutypaid),
      }));


      const payload = {
        total_duty_exbond_amount_inr: totalDutyExbond,
        total_weight: totalWeight,
        exbondchild: exbondchild,
      };

      // Helper function for API call
      const callApi = async () =>
        await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXBOND.CREATE}`, {
          method: "POST",
          headers: getHeaders(true),
          body: JSON.stringify(payload),
        });

      // Initial API call
      let response = await callApi();

      // -------------------------
      // 🔥 Handle 401 token expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry the same API call
          response = await callApi();
        } else {
          console.error("Refresh token failed. Logging out...");
          logout();
          return;
        }
      }

      const result = await response.json();

      if (result.status === 200 || result.status === 201) {
        toast.success("Exbond created successfully!");
        navigate("/ex-bond");
      } else {
        setWarningMessage(result.message || "Failed to create exbond");
        setWarningVisible(true);
      }
    } catch (error) {
      console.error("Error submitting exbond:", error);
      setWarningMessage("Failed to submit exbond. Please try again.");
      setWarningVisible(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative mx-auto w-full px-3 sm:px-4 md:px-6">
        <AddExbondheader />
        <Addexbondform
          formData={formData}
          updateFormData={updateFormData}
          records={records}
          setRecords={setRecords}
          resultantOptions={resultants}
          loadingResultants={loadingResultants}
          sectionOptions={sections}
          loadingSections={loadingSections}
          exbondExportOptions={exbondExportOptions}
          customerOptions={customers}
          loadingCustomers={loadingCustomers}
          biNumberOptions={binumber}
          loadingBINumber={loadingBINumber}
          materialOptions={materials}
          loadingMaterials={loadingMaterials}
          onBINumberChange={fetchMaterialInbondDate}
          onFetchWeightDuty={fetchWeightAndDutyByMaterial}
          availableWeights={availableWeights}
        />
        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
          <CancelButton
            className="w-full sm:w-auto"
            onClick={() => navigate("/ex-bond")}
          >
            Cancel
          </CancelButton>
          <Button
            onClick={handleSubmit}
            className="w-full sm:w-auto order-1 sm:order-2">
            Submit
          </Button>
        </div>
      </div>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}
    </>

  );
}